
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MdDelete, MdEdit } from 'react-icons/md';
import { fetchCoupons, deleteCoupon } from '../redux/actions/couponActions';
import Loader from "../components/shared/Loader";
import ConfirmDeleteModal from "../components/shared/ConfirmDeleteModalCoupon";
import AddCoupon from "../components/Coupon/AddCoupon"; 
import EditCoupon from "../components/Coupon/EditCoupon";

const Coupon = () => {
  const dispatch = useDispatch();
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [couponToDelete, setCouponToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [couponToEdit, setCouponToEdit] = useState(null);

  const { coupons, loading, error } = useSelector((state) => state.coupon);

  useEffect(() => {
    dispatch(fetchCoupons());
  }, [dispatch]);

  const openDeleteConfirmModal = (coupon) => {
    setCouponToDelete(coupon);
    setIsDeleteConfirmOpen(true);
  };

  const handleDelete = () => {
    if (couponToDelete) {
      dispatch(deleteCoupon(couponToDelete._id));
      setIsDeleteConfirmOpen(false);
    }
  };

  const openEditModal = (coupon) => {
    setCouponToEdit(coupon);
    setIsEditModalOpen(true);
  };

  const totalPages = Math.ceil((coupons?.length || 0) / itemsPerPage);
  const paginatedCoupons = coupons?.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Handle the case when error message contains "No coupons found"
  const isNoCouponsError = error && (error.includes('No coupons found') || error.includes('no coupons'));

  // Show loader while loading
  if (loading) return <Loader />;
  
  // Only show error for real errors (not "no coupons found")
  if (error && !isNoCouponsError) return <div className="p-4 text-red-500">Error: {error}</div>;

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4 sm:mb-0">Coupons Management</h1>
          <button 
            onClick={() => setIsAddModalOpen(true)}
            className="bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-lg transition-colors duration-200"
          >
            + Add New Coupon
          </button>
        </div>

        {/* Modals */}
        {isAddModalOpen && <AddCoupon onCloseModal={() => setIsAddModalOpen(false)} />}
        {isEditModalOpen && <EditCoupon coupon={couponToEdit} onCloseModal={() => setIsEditModalOpen(false)} />}
        {isDeleteConfirmOpen && couponToDelete && (
          <ConfirmDeleteModal
            onClose={() => setIsDeleteConfirmOpen(false)}
            onDelete={handleDelete}
            coupon={couponToDelete}
          />
        )}

        {/* Coupons Table - Show empty state with table structure */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-black text-white">
                <tr>
                  {['Name', 'Code', 'Value', 'Minimum Purchase', 'Type', 'Limit', 'Count', 'Status', 'Start Date', 'End Date', 'Actions'].map((header) => (
                    <th
                      key={header}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedCoupons?.length > 0 ? (
                  paginatedCoupons.map((item) => (
                    <tr key={item._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-900">{item.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono">{item.code}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {item.discountValue}{item.discountType === 'percentage' ? '%' : ''}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 capitalize">{item.minimumPurchase}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 capitalize">{item.discountType}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono">{item.usageLimit}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono">{item.usedCount}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium 
                          ${item.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {item.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(item.startDate).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(item.endDate).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm flex space-x-2">
                        <button
                          onClick={() => openEditModal(item)}
                          className="text-gray-500 hover:text-gray-700 transition-colors"
                        >
                          <MdEdit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => openDeleteConfirmModal(item)}
                          className="text-red-400 hover:text-red-600 transition-colors"
                        >
                          <MdDelete className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="px-4 py-8 text-center">
                      <div className="flex flex-col items-center justify-center space-y-3">
                        <svg 
                          className="w-12 h-12 text-gray-400" 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={2} 
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                          />
                        </svg>
                        <p className="text-gray-500 text-sm">
                          {error && isNoCouponsError ? "No coupons found" : "No coupons available"}
                        </p>
                        <button
                          onClick={() => setIsAddModalOpen(true)}
                          className="mt-2 bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-lg transition-colors duration-200 text-sm"
                        >
                          Create Your First Coupon
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Coupon;